import { record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';

import { getSelectedCityEnterprises } from '../../selectors/views/selectedCityEnterprises';
import { ApplicationState } from '../../types';
import { WithEnterpriseId } from '../../types/selectors/selectorPropTypes';
import { toDateTimeUtc } from '../../utils/dates';
import { getEnterpriseTimezone } from '../entities/enterprises/selectors';
import { getEnterpriseServiceId } from '../entities/services/selectors';
import { getEndDate, getSelectedHotelId, getStartDate } from '../resourceOrder/selectors';
import { getServiceAvailabilityRequestId } from '../serviceAvailability';
import { getAllServiceAvailabilityMeta } from '../serviceAvailability/selectors';

const defaultMeta = { isFetching: false, hasFailed: false };

const getCurrentEnterpriseAvailabilityMetas = (state: ApplicationState, { enterpriseId }: WithEnterpriseId) => {
    const startDate = getStartDate(state);
    const endDate = getEndDate(state);
    const serviceId = getEnterpriseServiceId(state, { enterpriseId });
    const enterpriseTimezone = getEnterpriseTimezone(state, { enterpriseId });

    if (startDate == null || endDate == null || enterpriseTimezone == null || serviceId == null) {
        return defaultMeta;
    }

    const requestId = getServiceAvailabilityRequestId({
        startUtc: toDateTimeUtc(startDate, enterpriseTimezone),
        endUtc: toDateTimeUtc(endDate, enterpriseTimezone),
        serviceId,
    });
    const metas = getAllServiceAvailabilityMeta(state);

    return metas[requestId] ?? defaultMeta;
};

const getCurrentServiceAvailabilityMeta = (state: ApplicationState) => {
    const enterpriseId = getSelectedHotelId(state);
    return getCurrentEnterpriseAvailabilityMetas(state, { enterpriseId });
};

export const isCurrentServiceAvailabilityFetching = flow(
    getCurrentServiceAvailabilityMeta,
    ({ isFetching }) => isFetching
);
export const hasCurrentServiceAvailabilityFailed = flow(
    getCurrentServiceAvailabilityMeta,
    ({ hasFailed }) => hasFailed
);

const getSelectedCityServiceAvailabilityMetas = (state: ApplicationState) =>
    pipe(
        getSelectedCityEnterprises(state, {}),
        record.map(({ id: enterpriseId }) => getCurrentEnterpriseAvailabilityMetas(state, { enterpriseId }))
    );

export const isSelectedCityServiceAvailabilityFetching = flow(
    getSelectedCityServiceAvailabilityMetas,
    record.some(({ isFetching }) => isFetching)
);

export const hasSelectedCityServiceAvailabilityFailed = flow(
    getSelectedCityServiceAvailabilityMetas,
    record.some(({ hasFailed }) => hasFailed)
);
